import { useCallback, useEffect, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { coresDoTema, TEXTO_COLORIDO_ESCURO } from "assets/coresTema";
import { conferirServidor, ouvirServidor } from "utils/servidorFora";
import { AnimacaoManutencao, AnimacaoSemConexao } from "./Animacoes";

const INTERVALO_S = 10;

const TEXTOS = {
  manutencao: {
    titulo: "Sistema em manutenção",
    texto: "O sistema está sendo atualizado ou reiniciado e volta em instantes. Esta tela fecha sozinha quando ele voltar, "
      + "e o que está aberto continua aqui. Se você estava salvando algo, confira depois se foi gravado.",
  },
  semConexao: {
    titulo: "Sem conexão com o sistema",
    texto: "Não foi possível falar com o servidor do sistema. Confira a internet ou a rede deste computador. "
      + "Esta tela fecha sozinha quando a conexão voltar, e o que está aberto continua aqui.",
  },
};

/**
 * Aviso de backend fora do ar (pedido da Aline em 26/10/2026; regra em utils/servidorFora.js). Montado uma vez no App.js,
 * por cima de tudo (login, telas, janelas abertas): confere o backend ao abrir o sistema e, fora do ar, a cada 10 s,
 * ao voltar a conexão do computador e pelo "Tentar agora". Quando o backend volta, fecha e avisa por alguns segundos.
 */
export default function ServidorFora() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const cores = coresDoTema(darkMode);
  const theme = useTheme();
  const [estado, setEstado] = useState("ok");
  const [conferindo, setConferindo] = useState(false);
  const [segundos, setSegundos] = useState(INTERVALO_S);
  const [voltou, setVoltou] = useState(false);
  const anterior = useRef("ok");

  const conferirAgora = useCallback(() => {
    setConferindo(true);
    conferirServidor().finally(() => {
      setConferindo(false);
      setSegundos(INTERVALO_S);
    });
  }, []);

  useEffect(() => {
    const parar = ouvirServidor((novo) => {
      if (novo === "ok" && anterior.current !== "ok") setVoltou(true);
      if (novo !== "ok") setVoltou(false);
      anterior.current = novo;
      setEstado(novo);
      setSegundos(INTERVALO_S);
    });
    // Ao abrir o sistema (inclusive no login): com o backend fora, o aviso já aparece
    conferirServidor();
    const aoVoltarConexao = () => conferirServidor();
    window.addEventListener("online", aoVoltarConexao);
    return () => {
      parar();
      window.removeEventListener("online", aoVoltarConexao);
    };
  }, []);

  // Fora do ar: contagem regressiva e nova conferência a cada INTERVALO_S segundos
  useEffect(() => {
    if (estado === "ok" || conferindo) return undefined;
    if (segundos <= 0) {
      conferirAgora();
      return undefined;
    }
    const relogio = setTimeout(() => setSegundos((s) => s - 1), 1000);
    return () => clearTimeout(relogio);
  }, [estado, segundos, conferindo, conferirAgora]);

  const aviso = TEXTOS[estado];
  const corIcone = darkMode ? TEXTO_COLORIDO_ESCURO.info : theme.palette.info.main;
  const corAlerta = darkMode ? TEXTO_COLORIDO_ESCURO.error : theme.palette.error.main;

  return (
    <>
      {aviso && (
        <MDBox
          role="alertdialog"
          aria-live="assertive"
          aria-labelledby="servidor-fora-titulo"
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 2,
            backgroundColor: "rgba(0, 0, 0, 0.45)",
            backdropFilter: "blur(2px)",
          }}
        >
          <MDBox
            sx={{
              width: "100%",
              maxWidth: 460,
              p: 3,
              borderRadius: 2,
              textAlign: "center",
              color: cores.texto,
              backgroundColor: cores.cartao,
              border: `1px solid ${cores.borda}`,
              boxShadow: "0 12px 32px rgba(0, 0, 0, 0.25)",
            }}
          >
            {estado === "manutencao"
              ? <AnimacaoManutencao cor={corIcone} corApoio={cores.textoFraco} />
              : <AnimacaoSemConexao cor={corIcone} corAlerta={corAlerta} corFundo={cores.cartao} />}
            <MDTypography id="servidor-fora-titulo" variant="h5" sx={{ color: "inherit", mt: 1.5 }}>
              {aviso.titulo}
            </MDTypography>
            <MDTypography variant="body2" sx={{ color: cores.textoSecundario, mt: 1.5 }}>
              {aviso.texto}
            </MDTypography>
            <MDBox mt={2.5} display="flex" alignItems="center" justifyContent="center" gap={1.5}>
              {conferindo ? (
                <>
                  <CircularProgress size={16} sx={{ color: corIcone }} />
                  <MDTypography variant="caption" sx={{ color: cores.textoFraco }}>Conferindo...</MDTypography>
                </>
              ) : (
                <MDTypography variant="caption" sx={{ color: cores.textoFraco }}>
                  {`Conferindo de novo em ${segundos} s`}
                </MDTypography>
              )}
              <MDButton variant="contained" color="info" size="small" disabled={conferindo} onClick={conferirAgora}>
                Tentar agora
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      )}
      <MDSnackbar
        color="success"
        icon="check"
        title="Sistema disponível"
        content="O sistema voltou. Se você estava salvando algo quando ele saiu do ar, confira se foi gravado."
        dateTime="agora"
        open={voltou}
        onClose={() => setVoltou(false)}
        close={() => setVoltou(false)}
        autoHideDuration={8000}
        bgWhite
      />
    </>
  );
}
